import { CheckCircleFilled, WarningFilled } from '@ant-design/icons'
import { defaultNumberDef } from '@components/_SiteWide/CellTemplates/typeColumnDefs'
import { addCommasToNumber, BBDTag, GraviGrid, GridControlBar, NothingMessage, Texto } from '@gravitate-js/excalibrr'
import { Switch } from 'antd'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { useValuation } from '../../../api/useValuation'
import { useUserSettings } from '../../../hooks/settings'
import { gridViewManager } from '@/utils/grid'

const STORAGE_KEY = 'inventory-history-selectors'

export function SwingReportSelectorsGrid({ setHeaderData, setSelectedICI }) {
  const gridAPIRef = useRef(null)
  const { updateSetting } = useUserSettings()
  const { getHistorySelectorsQuery } = useValuation()
  const { data: historySelectorsData, isFetching, isError } = getHistorySelectorsQuery()

  const onFilterExceptions = (checked) => {
    if (checked) {
      gridAPIRef?.current?.setFilterModel({ errors: { filterType: 'set', values: ['Yes'] } })
    } else {
      gridAPIRef?.current?.setFilterModel({ errors: {} })
    }
  }
  const getRowId = useCallback((row) => row.data.id, [])

  const agPropOverrides = useMemo(
    () => ({
      getRowId,
      enableRangeSelection: false,
      suppressCellFocus: true,
    }),
    []
  )
  const columnDefs = useMemo(() => getColumnDefs(), [])

  if (isError) return <NothingMessage title='No data found.' />

  return (
    <>
      <GridControlBar
        title='Inventory Rows'
        rowCount={historySelectorsData?.rows?.length}
        actionButtons={
          <>
            <Texto className='mr-2'>Filter To Exceptions</Texto>
            <Switch
              checkedChildren={<WarningFilled className='' />}
              onChange={onFilterExceptions}
              className='mr-2 error-switch'
              defaultChecked={false}
            />
          </>
        }
      />
      <GraviGrid
        style={{ width: '100%', height: '100%;' }}
        agPropOverrides={agPropOverrides}
        columnDefs={columnDefs}
        rowData={historySelectorsData?.rows}
        externalRef={gridAPIRef}
        storageKey={STORAGE_KEY}
        loading={isFetching}
        rowSelection='single'
        rowGroupPanelShow='onlyWhenGrouping'
        sideBar={null}
        onSelectionChanged={(props) => {
          const selected = props.api.getSelectedRows()[0]
          setSelectedICI(selected?.inventory_costing_identifier?.id)
          setHeaderData({
            site: selected?.inventory_costing_identifier?.site,
            product: selected?.inventory_costing_identifier?.product,
          })
        }}
        onFirstDataRendered={(params) => {
          params?.api?.getDisplayedRowAtIndex(0)?.setSelected(true)
        }}
        enableRangeSelection={false}
        onGridConfigChanged={(config) => updateSetting(`gridConfig::${STORAGE_KEY}`, config)}
        {...gridViewManager(STORAGE_KEY, updateSetting)}
      />
    </>
  )
}

const getColumnDefs = () => {
  return [
    {
      headerName: 'Site',
      field: 'inventory_costing_identifier.site',
      comparator: (a, b) => b - a,
      initialSort: 'desc',
      width: 120,
      flex: 0,
    },
    {
      field: 'errors',
      headerName: 'Alerts',
      width: 120,
      flex: 0,
      filterValueGetter: (params) => (params?.data?.errors > 0 ? 'Yes' : 'No'),
      cellRenderer: ({ value }) => {
        if (value && value > 0) {
          return (
            <BBDTag style={{ width: 'fit-content', backgroundColor: 'var(--theme-error)' }}>
              <Texto align='left' appearance='white' className='text-truncate p-0 m-0' style={{ width: 'fit-content' }}>
                <WarningFilled className='mr-2' />
                {addCommasToNumber(value, 0)}
              </Texto>
            </BBDTag>
          )
        }
        return <CheckCircleFilled className='text-success text-size-6 text-left' />
      },
    },
    {
      headerName: 'Product',
      field: 'inventory_costing_identifier.product',
      cellRenderer: (params) => {
        const product = params?.data?.inventory_costing_identifier?.product
        return (
          <Texto textTransform='uppercase' className='text-truncate'>
            {product}
          </Texto>
        )
      },
      comparator: (a, b) => {
        const orderBool = a > b
        return orderBool ? 1 : -1
      },
    },
    {
      field: 'total_gain_loss',
      headerName: 'Total Gain Loss',
      ...defaultNumberDef('total_gain_loss', 0),
      filter: 'agNumberColumnFilter',
      cellRenderer: ({ value }) => {
        return (
          <Texto className='text-truncate' align='right'>
            {addCommasToNumber(value, 0)}
          </Texto>
        )
      },
    },
  ]
}
